import { ContentCopy, DoneAll } from '@mui/icons-material';
import { ButtonBase, Tooltip, type SxProps, type Theme } from '@mui/material';
import { useEffect, useState } from 'react';
import { SECOND } from '../../constants';
import useLocalization from '../../hooks/useLocalization';
import { mergeSx } from '../../utils/styles';

const styles = {
  button: {
    p: 0.5,
    borderRadius: 1,
    flexShrink: 0,
  },
  icon: {
    fontSize: { xs: 16, md: 20 },
  },
};

type CopyButtonProps = {
  onCopy: () => Promise<boolean>;
  label?: string;
  copyLabel?: string;
  sx?: SxProps<Theme>;
};

const CopyButton = ({ onCopy, label, copyLabel, sx }: CopyButtonProps) => {
  const { t } = useLocalization();
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) {
      return;
    }

    const timeout = setTimeout(() => setCopied(false), 3 * SECOND);

    return () => clearTimeout(timeout);
  }, [copied]);

  const handleCopy = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setCopied(await onCopy());
  };

  return (
    <Tooltip title={copyLabel ?? t('copy')}>
      <ButtonBase
        type='button'
        aria-label={copyLabel ?? label ?? t('copy')}
        aria-pressed={copied}
        onClick={handleCopy}
        sx={mergeSx(styles.button, sx)}
      >
        {copied ? <DoneAll sx={styles.icon} /> : (label ?? <ContentCopy sx={styles.icon} />)}
      </ButtonBase>
    </Tooltip>
  );
};

export default CopyButton;
